import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Chapter, Prose, TheoryBox, MTQuote } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";

export default function Beatles11() {
  const { lesson, course, accent } = findLesson("the-beatles", "11");
  return (
    <LessonShell
      course={course}
      courseSlug="the-beatles"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <Chapter eyebrow="The concept" title="Harmonic pillars — chords on the strong beat">
        <Prose>
          <p>
            "Hey Jude" is the vehicle for one of Jason's most important compositional ideas:
            the <strong>harmonic pillar</strong>. A harmonic pillar is a chord played such that
            it always lands at the strong beats of the bar — and most commonly, at beat 1. The
            chord changes happen evenly, in a cycle — "like a walk around a park so to speak where
            stuff happens and you have an adventure of sorts."
          </p>
          <p>
            In "Hey Jude," Jason counts: "4, change, 2, 3, 4, change, 2." The chords move
            predictably through F major, C major, B♭ major, and back to F major, each change
            arriving evenly on a strong beat.
          </p>
          <p>
            The verse chords of "Hey Jude" are the I, IV, and V of F major: F, B♭, and C. As Jason
            notes: "we've not really drifted beyond the one major, the four major and the five
            major." Three major chords, moving like a cycle, creating "a journey. You're moving
            and then you turn."
          </p>
        </Prose>

        <TheoryBox label="Quick stats">
          <strong>Key:</strong> F major ·{" "}
          <strong>Meter:</strong> 4/4 ·{" "}
          <strong>Verse chords:</strong> F (I), C (V), B♭ (IV) — the three major chords of F (the
          pre-chorus adds Dm and Gm7, and the long coda adds E♭) ·{" "}
          <strong>Harmonic pillar rule:</strong> every chord lands on a strong beat — usually
          beat 1 — and the changes come evenly
        </TheoryBox>
      </Chapter>

      <YouTubeEmbed
        videoId="A_MjCqQoLLA"
        title="Hey Jude — The Beatles"
        caption="Count the chord changes from the very first bar. Each chord arrives on a strong beat and the changes come evenly. That even, stable movement is the harmonic pillar — the foundation the melody rests on."
      />

      <Chapter eyebrow="The melody" title="Landing on chord tones — and making them long">
        <Prose>
          <p>
            The melody of "Hey Jude" is built on a principle that Jason explains carefully:
            the composer is not just landing on chord tones — he is also <strong>making those
            landings long</strong>. Both things together. "He's not only landing at the chord
            tones, he's also making the land to be long. That means the human ear will acknowledge
            that note a lot more in the vicinity of all the other colourful notes which are
            passing notes."
          </p>
          <p>
            The passing notes connect the harmonic landings. They are colourful. They move quickly.
            But the ear does not hold onto them — it holds onto the long sustained note that sits
            on the chord tone. That held note is what the listener remembers. That held note is
            the melody.
          </p>
          <p>
            Jason works through specific examples: On the F major chord, the singer "could have
            landed at any of these chord tones. Hey Jude — which he did. He landed at the A."
            F major has three notes: F, A, C. The composer chose A. He could have chosen F or C.
            That choice — of the third, the warmest note of the chord — gives the opening its
            particular emotional quality.
          </p>
          <p>
            Then, at the end of the first line, the last word lands on G over the C major chord —
            G is the fifth of C major. "Bad is G and as you can see C major chord clearly has a G
            in it so works quite well." At the end of the second line, the long note lands back
            on the F major chord. Every important word in the lyric lands on a chord tone and holds.
          </p>
        </Prose>

        <RomanNumeralAnalysis
          label="Hey Jude — verse, F major harmonic pillars"
          caption="As on Jason's chord chart: F → C → C7 → C7sus4 → F, then B♭ → F → C7 → F. Each chord is a pillar — stable and changing evenly. The melody lands on chord tones and holds them long."
          chords={[
            { chord: "F", rn: "I", function: "Tonic" },
            { chord: "C", rn: "V", function: "Dominant" },
            { chord: "C7", rn: "V7", function: "Dominant" },
            { chord: "C7sus4", rn: "V7sus4", function: "Dominant" },
            { chord: "F", rn: "I", function: "Tonic" },
            { bar: true },
            { chord: "Bb", rn: "IV", function: "Pre-dominant" },
            { chord: "F", rn: "I", function: "Tonic" },
            { chord: "C7", rn: "V7", function: "Dominant" },
            { chord: "F", rn: "I", function: "Tonic" },
          ]}
        />

        <TheoryBox label="Melody landing points — Hey Jude">
          "Hey Jude" (F major chord) → melody lands on A. A is the third of F major — a chord tone. ·{" "}
          End of line one (C major chord) → lands on G. G is the fifth of C major — a chord tone. ·{" "}
          End of line two (back on F major) → the long note lands on the F major chord and
          holds. ·{" "}
          The rule: land on chord tones at strong beats and make them long. Passing notes connect
          between landings. The ear remembers the long notes, not the passing ones.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The chicken and the egg" title="Which came first — the melody or the chords?">
        <Prose>
          <p>
            Jason raises the question that every songwriter eventually confronts: did the melody
            come first, or did the chords come first? "It's a chicken and egg situation. You don't
            know whether the melody came first or whether the chords came first."
          </p>
          <p>
            For "Hey Jude," we don't know. If it was composed in the shower, it was probably
            the tune first — because that's all you can do in the shower. If McCartney was
            sitting at a piano with some chords, the chords may have given birth to the melody.
            Or maybe it started with a drum groove, or with the lyrics. "We don't know what
            started it off."
          </p>
          <p>
            But here is Jason's key observation about why that question matters for students: "developing
            this chicken and egg situation is it makes the composer really good at what they are
            doing. One guy is able to pretty much incorporate everything into his or her work. He has his
            chords ready. If he sings a note, he knows where to land on which chord or vice versa.
            If he has chords, he'll know what melody goes with the piece."
          </p>
          <p>
            Practicing both directions — melody first, then chords; chords first, then melody — is
            how you build that skill. Once you can move fluently in both directions, you no longer
            need to start from one fixed point. The song can begin anywhere.
          </p>
        </Prose>

        <TheoryBox label="Lyrics, melody, harmony, rhythm — all at once">
          Jason lists the four elements a songwriter must weave together: lyrics, melody, harmony,
          rhythm. "One person is doing this real time." Lyrics land on important words — adjectives,
          proper nouns. Those important words fall on beat 1. Even without music theory, a speaker
          naturally emphasises key words in a sentence. The songwriter uses the same instinct.
          "Lyrically also there's lot going on."
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The exercise" title="Ruin the melody — make your own">
        <Prose>
          <p>
            Jason's practical instruction for working with "Hey Jude": "Your work should ideally
            be to deconstruct the song and rather than reharmonize it, just keep the same chords."
            And: "The whole idea with this exercise is to take a great song, which is already
            there. Great chords. Well, it's also a great melody. Unfortunately, your job is now to
            sort of ruin that melody. And make your own melody over that."
          </p>
          <p>
            Why ruin it? Because writing your own melody over the same chords forces you to make
            the same choices the composer made: which chord tone to land on, how long to hold it,
            which passing notes to use to get there. You discover the melody construction from the
            inside. "And even if you can't change the song too much, at least acknowledge the fact
            that most great songs are written like this. You'll have a chord sequence, we have a
            melody."
          </p>
          <p>
            A piano solo version of the song, played purely instrumentally, is what Jason demonstrates.
            The chord sequence is untouched. The melody is his own invention over those chords —
            chord tones landing at the strong beats, passing notes connecting them, rhythm created
            by how long each note holds. The same principles. Different music.
          </p>
        </Prose>

        <NoteImage
          src="/notes/the-beatles/finals/24-hey-jude.png"
          caption="Jason's handwritten Hey Jude chart in F major — verse (F, C, C7, C7sus4, B♭, C7), pre-chorus (F7, B♭, Dm/A, Gm7, Dm/F, C/E, C7), and the outro, where the horns play F → E♭ → B♭ → F."
        />

        <MTQuote>
          "So hopefully you've understood some aspects of this and you can always ask me doubts
          if you have further questions on any of these subjects. Right? All the best composing
          music now. Go for it." — Jason, closing the Hey Jude lesson.
        </MTQuote>
      </Chapter>
    </LessonShell>
  );
}
